/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * AssignmentListMainComponent
 * Main component to show lis of assignments/exams or anything extracted from syllabus
 */



import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { useSyllabus } from '../../contexts/SyllabusContext';
import useAssignmentStyles from '../styles/AetAssignmentStyles';
import Utils from '../../common/utils';
import {
  ASSIGNMENT_FORM_ACTIONS,
  PREFIX_ASSIGNMENT,
  LOCAL_DELETE_TYPE,
  DATE_NORMALIZATION_ACTION
} from '../../common/constants';
import AddEditAssignmentForm from './AddEditAssignmentForm';
import Tabular, { TextChipTemplate, DateTemplate } from '../tabular/Tabular';
import DynamicToggle from '../common/DynamicToggle';
import DeleteButton from '../common/DeleteButton';
import ValidationUtils from '../../common/validationUtils';
import ConfirmationCard from '../common/ConfirmationCard';
import { wallClockToUTC, combineLocalDateTime } from '../../common/dateTimeUtils';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import Icon from '../icons/Icon';

const
  AssignmentListMainComponent = ({
    parentApp,
    textInfo,
    tocData,
    chapterMcqMapping,
    onUploadComplete,
    onSaveAndAdd,
    renderInModal = false,
    handleAddAssignemntPopupClose,
    editingAssignment = null,
    editingIndex = null,
    onEditComplete,
    onDeleteComplete,
    assignmentFormActions,
    showListView,
    restrictAlreadyAssigned,
    currentAssignmentIdx,
    addAssignmentCount,
    onDeleteReady,
    isBulkEditMode,
    showPublishButton,
    handleUploadingScreenChange,
    currentAssignedList,
    isLoading = false,
    timeZoneOptions,
    excludeTitles,
    showAssignmentTitle,
    titleValidationConfig,
    assignmentConfig = {},
    ...props

  }) => {
    const classes = Utils.getSyllabusStyles(
      parentApp,
      { aet: useAssignmentStyles, onereader: useAssignmentStyles },
      useAssignmentStyles
    );
    const { state, actions } = useSyllabus();
    const assignments = state.assignments || [];

    const [formKey, setFormKey] = useState(0);
    const [localEditingAssignment, setLocalEditingAssignment] = useState(null);
    const [localEditingIndex, setLocalEditingIndex] = useState(null);
    const [showAddAnotherForm, setShowAddAnotherForm] = useState(false);

    const [selectedIndices, setSelectedIndices] = useState([]);
    const [showCheckboxes, setShowCheckboxes] = useState(false);
    const [localAssignmentCount, setLocalAssignmentCount] = useState(addAssignmentCount || 0);
    const [validationResult, setValidationResult] = useState({
      isValid: true,
      invalidAssignments: []
    });

    const [deleteConfirmationState, setDeleteConfirmationState] = useState({
      open: false,
      type: null, // 'bulk' or 'single',
      callback: () => {},
      isBulkDelete: false
    });

    const showDeleteConfirmation = (type, callback = () => {}) => {
      setDeleteConfirmationState({
        open: true,
        type,
        callback,
        isBulkDelete: type === LOCAL_DELETE_TYPE.BULK && selectedIndices.length > 1
      });
    };

    // Close handler
    const closeDeleteConfirmation = () => {
      setDeleteConfirmationState(prev => ({
        ...prev,
        open: false,
        callback: () => {}
      }));
    };

    // guard added
    const handleDeleteSelected = (callbackOrEvent) => {
      if (selectedIndices.length === 0) return;
      const callback = typeof callbackOrEvent === 'function' ? callbackOrEvent : () => {};
      showDeleteConfirmation(LOCAL_DELETE_TYPE.BULK, callback);
    };

    const activeEditingAssignment = editingAssignment || localEditingAssignment;
    const activeEditingIndex = editingIndex || localEditingIndex;
    const isEditingFromProps = editingAssignment !== null;
    let isValidationRequired = false;
    const currentAssignmentIdxForRestrict = currentAssignmentIdx ?? localEditingIndex;
    let assignmentsToUse = state?.currentAssignedList || state?.assignments || [];
    const isConfirmationOrBulkEdit = showPublishButton || isBulkEditMode;

    if (showPublishButton && currentAssignedList?.length > 0) {
      assignmentsToUse = currentAssignedList;
    }

    if (showPublishButton || isBulkEditMode) {
      isValidationRequired = !!activeEditingAssignment;
    }

    useEffect(() => {
      setLocalAssignmentCount(addAssignmentCount);
    }, [addAssignmentCount]);

    useEffect(() => {
      setShowCheckboxes(props?.showCheckboxes || false);
      // Clear selection when checkboxes are hidden
      if (!props?.showCheckboxes) {
        setSelectedIndices([]);
      }
    }, [props?.showCheckboxes]);

    useEffect(() => {
      if (isConfirmationOrBulkEdit && assignments.length > 0) {
        const result = ValidationUtils.validateAssignments(assignments, tocData, chapterMcqMapping);
        setValidationResult(result);
        // Pass validation result to parent if callback provided
        if (props.onValidationChange) {
          props.onValidationChange(result);
        }
      }
    }, [assignments.length, showPublishButton,
      tocData, state.assignments,
      chapterMcqMapping]);


    // Handle delete selected items
    const executeDeleteSelected = () => {
      if (selectedIndices.length === 0) return;
      const sortedIndices = [...selectedIndices].sort((a, b) => b - a);
      sortedIndices.forEach(index => {
        actions.deleteAssignment(index);
      });

      // Update syllabus info and call callbacks
      const remainingAssignments = assignments.filter((_, idx) => !selectedIndices.includes(idx));

      // Validate and mark assignments with validation flags
      const currentValidation = isConfirmationOrBulkEdit
        ? ValidationUtils.validateAssignments(remainingAssignments, tocData, chapterMcqMapping)
        : { isValid: true, invalidAssignments: [], invalidCount: 0 };
      const validatedAssignments = remainingAssignments.map((assignment, idx) => {
        const invalidItem = currentValidation.invalidAssignments.find(inv => inv.index === idx);

        return {
          ...assignment,
          isValid: !invalidItem,
          validationErrors: invalidItem?.errors || []
        };
      });

      const syllabusInfo = state.syllabusInfo ? {
        ...state.syllabusInfo,
        assignments: validatedAssignments,
        data: {
          ...state.syllabusInfo.data,
          schedule: validatedAssignments
        },
        validation: {
          isValid: currentValidation.isValid,
          totalCount: validatedAssignments.length,
          validCount: validatedAssignments.length - currentValidation.invalidCount,
          invalidCount: currentValidation.invalidCount,
          invalidAssignments: currentValidation.invalidAssignments
        }
      } : {
        id: 'manual-assignment-delete',
        assignments: validatedAssignments,
        data: { schedule: validatedAssignments },
        validation: {
          isValid: true,
          totalCount: validatedAssignments.length,
          validCount: validatedAssignments.length,
          invalidCount: 0,
          invalidAssignments: []
        }
      };
      onUploadComplete({
        syllabusInfo,
        assignments: validatedAssignments
      });

      setSelectedIndices([]);
    };

    useEffect(() => {
      if (onDeleteReady) {
        onDeleteReady({
          deleteSelected: handleDeleteSelected,
          selectedCount: selectedIndices.length
        });
        handleUploadingScreenChange(false);
      }
    }, [selectedIndices.length]);

    const activeEditingIndexForForm = useMemo(() => {
      const rawExcludeIndex = currentAssignmentIdxForRestrict ?? activeEditingIndex;
      if (rawExcludeIndex !== null && rawExcludeIndex !== undefined && !Number.isNaN(Number(rawExcludeIndex))) {
        const numValue = Number(rawExcludeIndex);
        if (Number.isInteger(numValue)) {
          return numValue;
        }
      }
      if (activeEditingIndex !== null
        && activeEditingIndex !== undefined
        && !Number.isNaN(Number(activeEditingIndex))) {
        const numValue = Number(activeEditingIndex);
        if (Number.isInteger(numValue)) {
          return numValue;
        }
      }

      return null;
    }, [currentAssignmentIdxForRestrict, activeEditingIndex]);

    const handleSaveAndAdd = (assignmentData) => {
      if (onSaveAndAdd || onUploadComplete) {
        // ALWAYS pass only the single current assignment (assignmentData) in the same format
        // assignmentData = the current assignment being saved, NOT from state
        let syllabusInfo;
        if (state.syllabusInfo) {
          syllabusInfo = {
            ...state.syllabusInfo,
            data: {
              ...state.syllabusInfo.data,
              schedule: [assignmentData] // Single current assignment only
            }
          };
        } else {
          syllabusInfo = {
            id: 'manual-assignment-mode',
            data: {
              schedule: [assignmentData] // Single current assignment only
            }
          };
        }

        const combinedData = {
          syllabusInfo,
          assignments: [assignmentData] // Single current assignment only
        };
        onSaveAndAdd(combinedData, { action: ASSIGNMENT_FORM_ACTIONS.SAVE_AND_ADD });
      }
      setLocalAssignmentCount(prev => prev + 1);
      setFormKey(prev => prev + 1);
      setShowAddAnotherForm(true);
    };

    const handleEditClose = () => {
      if (isEditingFromProps && handleAddAssignemntPopupClose) {
      // If editing from props, call parent close handler
        handleAddAssignemntPopupClose();
      } else {
      // If editing from local state, clear local state
        setLocalEditingAssignment(null);
        setLocalEditingIndex(null);
      }
    };

    // Public handler that shows confirmation first
    const handleLocalDelete = () => {
      showDeleteConfirmation(LOCAL_DELETE_TYPE.SINGLE);
    };

    const executeLocalDelete = () => {
      // Delete the assignment from context
      if (localEditingIndex !== null) {
        actions.deleteAssignment(localEditingIndex);
        const updatedAssignments = state.assignments.filter((_, idx) => idx !== localEditingIndex);

        // Validate and mark assignments with validation flags
        const currentValidation = isConfirmationOrBulkEdit
          ? ValidationUtils.validateAssignments(updatedAssignments, tocData, chapterMcqMapping)
          : { isValid: true, invalidAssignments: [], invalidCount: 0 };
        const validatedAssignments = updatedAssignments.map((assignment, idx) => {
          const invalidItem = currentValidation.invalidAssignments.find(inv => inv.index === idx);

          return {
            ...assignment,
            isValid: !invalidItem,
            validationErrors: invalidItem?.errors || []
          };
        });

        const syllabusInfo = state.syllabusInfo ? {
          ...state.syllabusInfo,
          data: {
            ...state.syllabusInfo.data,
            schedule: validatedAssignments
          },
          validation: {
            isValid: currentValidation.isValid,
            totalCount: validatedAssignments.length,
            validCount: validatedAssignments.length - currentValidation.invalidCount,
            invalidCount: currentValidation.invalidCount,
            invalidAssignments: currentValidation.invalidAssignments
          }
        } : {
          id: 'manual-assignment-delete',
          data: { schedule: validatedAssignments },
          validation: {
            isValid: true,
            totalCount: validatedAssignments.length,
            validCount: validatedAssignments.length,
            invalidCount: 0,
            invalidAssignments: []
          }
        };

        onUploadComplete({
          syllabusInfo,
          assignments: validatedAssignments
        });
      }
      // Clear the local editing state
      setLocalEditingAssignment(null);
      setLocalEditingIndex(null);
    };

    // Confirm handler - executes the actual delete based on type
    const handleConfirmDelete = () => {
      if (deleteConfirmationState.type === LOCAL_DELETE_TYPE.BULK) {
        executeDeleteSelected();
      } else if (deleteConfirmationState.type === LOCAL_DELETE_TYPE.SINGLE) {
        executeLocalDelete();
      }
      closeDeleteConfirmation();
      deleteConfirmationState.callback();
    };

    const handleSaveAndClose = (assignmentData, action) => {
      setShowAddAnotherForm(false);
      if (onUploadComplete || onSaveAndAdd) {
        const isLocalAdd = showPublishButton && !activeEditingAssignment;

        const currentAssignments = (activeEditingAssignment && activeEditingIndex !== null)
          ? state.assignments.map((item, idx) => (idx === activeEditingIndex ? assignmentData : item))
          : [...(state.assignments || []), assignmentData];

        // Validate and mark assignments with validation flags
        const currentValidation = isConfirmationOrBulkEdit
          ? ValidationUtils.validateAssignments(currentAssignments, tocData, chapterMcqMapping)
          : { isValid: true, invalidAssignments: [], invalidCount: 0 };
        const validatedAssignments = currentAssignments.map((assignment, idx) => {
          const invalidItem = currentValidation.invalidAssignments.find(inv => inv.index === idx);

          return {
            ...assignment,
            isValid: !invalidItem,
            validationErrors: invalidItem?.errors || []
          };
        });

        let syllabusInfo;
        if (state.syllabusInfo) {
        // Update existing syllabusInfo with current assignments
          syllabusInfo = {
            ...state.syllabusInfo,
            data: {
              ...state.syllabusInfo.data,
              schedule: isLocalAdd ? [assignmentData] : validatedAssignments
            },
            validation: {
              isValid: currentValidation.isValid,
              totalCount: validatedAssignments.length,
              validCount: validatedAssignments.length - currentValidation.invalidCount,
              invalidCount: currentValidation.invalidCount,
              invalidAssignments: currentValidation.invalidAssignments
            }
          };
        } else {
        // Create mock syllabusInfo for first time manual assignment
          syllabusInfo = {
            id: 'manual-assignment-mode',
            data: {
              schedule: isLocalAdd ? [assignmentData] : validatedAssignments
            },
            validation: {
              isValid: true,
              totalCount: validatedAssignments.length,
              validCount: validatedAssignments.length,
              invalidCount: 0,
              invalidAssignments: []
            }
          };
        }
        handleEditClose();

        const combinedData = {
          syllabusInfo,
          assignments: isLocalAdd ? [assignmentData] : validatedAssignments
        };
        if (editingAssignment !== null && action === ASSIGNMENT_FORM_ACTIONS.DELETE) {
          onDeleteComplete(combinedData);
        } else if (editingAssignment !== null && action === ASSIGNMENT_FORM_ACTIONS.SAVE) {
          onEditComplete(combinedData);
        } else if (showListView && !isLocalAdd) {
          // to be called here only in preview, not dashboard
          onUploadComplete(combinedData);
        } else {
          onSaveAndAdd(combinedData, { action: ASSIGNMENT_FORM_ACTIONS.SAVE_AND_CLOSE });
        }
      }
    };

    const handleEditClick = (assignment, index) => {
      setLocalEditingAssignment(assignment);
      setLocalEditingIndex(index);
    };

    // Memoize tocData to prevent form data wipe
    const memoizedTocData = useMemo(() => {
      const rawExcludeIndex = currentAssignmentIdxForRestrict ?? activeEditingIndex;
      const excludeIndex = rawExcludeIndex !== null && rawExcludeIndex !== undefined
        ? Number(rawExcludeIndex)
        : null;
      const assignmentsForRestriction =
        Number.isInteger(excludeIndex)
          ? assignmentsToUse.filter((_, idx) => idx !== excludeIndex)
          : assignmentsToUse;
      const assignedIdsForRestriction = (assignmentsForRestriction || [])
        .flatMap(a => {
          if (!a?.textbookCoverage) return [];
          const expandedNodes = Utils.rehydrateSelectedNodesFromCoverage(
            a.textbookCoverage,
            tocData,
            { preferPages: true }
          );

          return expandedNodes.map(node => node.id).filter(Boolean);
        })
        .filter(Boolean);

      const tocWithFlagsExcludingCurrent = Utils.markAssignedSections(tocData, assignedIdsForRestriction);

      return restrictAlreadyAssigned ? tocWithFlagsExcludingCurrent : tocData;
    }, [currentAssignmentIdxForRestrict, activeEditingIndex, assignmentsToUse, tocData, restrictAlreadyAssigned]);

    // Transform assignments data for Tabular
    const tabularData = useMemo(() => assignments.map((assignment, index) => {
      const invalidItem = validationResult.invalidAssignments.find(
        inv => inv.index === index
      );
      const assignmentErrors = invalidItem?.errors || [];
      const rowStatus = ValidationUtils.getAssignmentRowStatus(assignment, textInfo, assignmentErrors);
      const hasNoChaptersError = ValidationUtils.hasNoChaptersError(assignmentErrors);
      const requiresManualDateEntry = assignment.dateNormalizationAction
        === DATE_NORMALIZATION_ACTION.MANUAL_ENTRY_REQUIRED;

      return {
        ...assignment,
        id: `${PREFIX_ASSIGNMENT}-${index}`,
        originalIndex: index,
        hasError: !!invalidItem,
        hasNoChaptersError,
        requiresManualDateEntry,
        rowStatusMessage: assignmentConfig?.showErrorStatus ? (rowStatus?.message || '') : '',
        rowStatusIcon: rowStatus?.icon || null,
        errorTypes: invalidItem?.errors.map(e => e.type) || []
      };
    }), [assignments, validationResult, textInfo]);

    const showStatusColumn = assignmentConfig?.showErrorStatus && (showPublishButton || isBulkEditMode) && assignments.some((row, index) => {
      const rowErrors = validationResult.invalidAssignments.find(inv => inv.index === index)?.errors || [];

      return row.dateNormalizationAction
        || row.coverageDedupeAction
        || ValidationUtils.hasNoChaptersError(rowErrors);
    });

    // Define columns for Tabular
    const columns = [
      {
        title: textInfo.assignmentTitle,
        render: (assignment) => {
          const chapterTags = Utils.getAssignmentChaptersNumbers(assignment.chapters || []) || [];

          return (
            <TextChipTemplate
              id={assignment.id}
              title={assignment.title}
              sections={chapterTags}
              label={textInfo.sectionsLabel}
              showEditIcon
              editIconTooltip={textInfo.editAssignmentLabel}
              validationInfoToolTip={textInfo.validationInfoToolTip}
              onEditClick={() => handleEditClick(assignment, assignment.originalIndex)}
              hasError={assignment.hasError}
            />
          );
        }
      },
      {
        title: textInfo.completionLabel,
        cellClassName: classes.assignmentCompletionColumn,
        render: () => (
          <div>{/* Completion progress - empty for now */}</div>
        )
      },
      ...(showStatusColumn ? [{
        title: textInfo.statusLabel,
        render: (assignment) => {
          if (!assignment.rowStatusMessage) {
            return null;
          }

          return (
            <TooltipWrapper tooltip={assignment.rowStatusMessage} placement="top">
              <div className={classes.assignmentStatusCell}>
                {assignment.rowStatusIcon === 'info' && (
                  <Icon name="InfoIcon" className={classes.assignmentStatusInfoIcon} />
                )}
                {assignment.rowStatusIcon === 'warning' && (
                  <WarningIcon className={classes.assignmentStatusWarningIcon} />
                )}
                <Typography className={classes.assignmentStatusText} component="span">
                  {assignment.rowStatusMessage}
                </Typography>
              </div>
            </TooltipWrapper>
          );
        }
      }] : []),
      {
        title: textInfo.dueDateLabel,
        render: (assignment) => {
          // Respect the associated timezone in syllabus items and convert accordingly
          const dueDateISO = assignment?.timeZone
            ? wallClockToUTC(assignment?.date, assignment?.time, assignment?.timeZone)?.toISOString()
            : combineLocalDateTime(assignment?.date, assignment?.time);

          return (
            <div className={classes.dateTemplateWrapper}>
              <DateTemplate date={dueDateISO} timeZoneOptions={timeZoneOptions} />
            </div>
          );
        }
      }
    ];

    // Handle checkbox selection
    const handleSelect = (selectedItems) => {
      const indices = selectedItems
        .map(item => item.originalIndex)
        .filter(idx => idx !== undefined);
      setSelectedIndices(indices);
    };

    const renderAssignmentsListHeader = () => {
      if (!isBulkEditMode) {
        return null;
      }

      return (
        <div className={classes.assignmentsListHeader}>
          <div className={classes.leftAlign}>
            <Typography className={classes.assignmentsListHeaderTitle}>
              {textInfo.dulkEditTitle} ({assignments.length})
            </Typography>
            {showCheckboxes && (
            <>
              <DeleteButton
                tooltipText={textInfo.tooltip}
                badgeCount={selectedIndices.length}
                iconName="DeleteActionIcon"
                disabled={!(selectedIndices.length > 0)}
                callbackFunc={handleDeleteSelected}
              />
            </>
            )}
          </div>

          <div className={classes.assignmentHeaderActions}>
            <DynamicToggle
              label={textInfo.dynamicToggleLabel}
              value={showCheckboxes}
              onChange={(value) => {
                setShowCheckboxes(value);
                if (!value) {
                  setSelectedIndices([]);
                }
              }}
            />

          </div>
        </div>
      );
    };

    const getMappedTabularData = useMemo(() => Utils.getTabularData(tabularData, tocData), [tabularData, tocData]);

    const getDeleteConfirmationText = () => {
      const { isBulkDelete = false } = deleteConfirmationState;

      return {
        title: isBulkDelete ? textInfo.deleteConfirmationTitleBulk : textInfo.deleteConfirmationTitleSingle,
        content: isBulkDelete ? textInfo.deleteConfirmationSubtitleBulk : textInfo.deleteConfirmationSubtitleSingle,
        cancelText: isBulkDelete ? textInfo.deleteConfirmationCancelTextBulk : textInfo.deleteConfirmationCancelTextSingle
      };
    };

    return (
      <>
        <div className={classes.assignmentsList}>
          {renderAssignmentsListHeader()}
        </div>

        {showListView && assignments.length > 0 && (
        <Tabular
          data={getMappedTabularData}
          columns={columns}
          config={{
            showCheckBox: showCheckboxes,
            showColumnDivider: false,
            tableClassName: classes.assignmentListTable
          }}
          onSelect={handleSelect}
          onRowClick={(item) => {
            if (!showCheckboxes) {
              handleEditClick(item, item.originalIndex);
            }
          }}
        />
        )}

        {/* this is triggered on click of add or editassignment button */}
        {((assignments.length === 0 && !renderInModal) || showAddAnotherForm || renderInModal) &&
          <AddEditAssignmentForm
            open
            key={formKey}
            onClose={() => {
              setShowAddAnotherForm(false);
              handleAddAssignemntPopupClose();
            }}
            parentApp={parentApp}
            textInfo={textInfo}
            assignmentConfig={assignmentConfig}
            tocData={memoizedTocData}
            chapterMcqMapping={chapterMcqMapping}
            renderInModal={renderInModal}
            editingAssignment={editingAssignment}
            editIndex={editingIndex}
            onSaveAndAdd={activeEditingAssignment ? handleSaveAndClose : handleSaveAndAdd}
            onSaveAndClose={handleSaveAndClose}
            assignmentFormActions={assignmentFormActions}
            activeEditingIndex={activeEditingIndexForForm}
            addAssignmentCount={localAssignmentCount}
            isValidationRequired={isValidationRequired}
            isLoading={isLoading}
            timeZoneOptions={timeZoneOptions}
            excludeTitles={excludeTitles}
            showAssignmentTitle={showAssignmentTitle}
            titleValidationConfig={titleValidationConfig}
          />}


        {/* this is triggered on rowclick of the assignment list */}
        {localEditingAssignment && !isEditingFromProps && (
        <AddEditAssignmentForm
          open
          key={formKey}
          onClose={handleEditClose}
          editingAssignment={localEditingAssignment}
          editIndex={localEditingIndex}
          parentApp={parentApp}
          textInfo={textInfo}
          assignmentConfig={assignmentConfig}
          tocData={memoizedTocData}
          chapterMcqMapping={chapterMcqMapping}
          renderInModal
          onSaveAndAdd={handleSaveAndClose}
          onSaveAndClose={handleSaveAndClose}
          onDelete={handleLocalDelete}
          assignmentFormActions={assignmentFormActions}
          isLocalEditing
          activeEditingIndex={activeEditingIndexForForm}
          isValidationRequired={isValidationRequired}
          timeZoneOptions={timeZoneOptions}
          excludeTitles={excludeTitles}
          showAssignmentTitle={showAssignmentTitle}
          titleValidationConfig={titleValidationConfig}
        />
        )}
        {/* Delete Confirmation Dialog */}
        <ConfirmationCard
          open={deleteConfirmationState.open}
          onClose={closeDeleteConfirmation}
          title={getDeleteConfirmationText()?.title}
          content={getDeleteConfirmationText()?.content}
          cancelText={getDeleteConfirmationText()?.cancelText}
          confirmText={textInfo.deleteConfirmationConfirmText}
          onCancel={closeDeleteConfirmation}
          onConfirm={handleConfirmDelete}
          showCloseButton
        />
      </>
    );
  };

AssignmentListMainComponent.propTypes = {
  parentApp: PropTypes.string,
  textInfo: PropTypes.object,
  assignmentConfig: PropTypes.object,
  tocData: PropTypes.array.isRequired,
  chapterMcqMapping: PropTypes.object,
  onUploadComplete: PropTypes.func,
  onSaveAndAdd: PropTypes.func,
  renderInModal: PropTypes.bool,
  editingAssignment: PropTypes.object,
  editingIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleAddAssignemntPopupClose: PropTypes.func,
  onEditComplete: PropTypes.func,
  onDeleteComplete: PropTypes.func,
  isBulkEditMode: PropTypes.bool,
  assignmentFormActions: PropTypes.shape({
    save: PropTypes.string,
    saveAndAdd: PropTypes.string,
    delete: PropTypes.string,
    saveAndClose: PropTypes.string
  }),
  showListView: PropTypes.bool,
  restrictAlreadyAssigned: PropTypes.bool,
  currentAssignmentIdx: PropTypes.number,
  addAssignmentCount: PropTypes.number,
  showCheckboxes: PropTypes.bool,
  onDeleteReady: PropTypes.func,
  showPublishButton: PropTypes.bool,
  handleUploadingScreenChange: PropTypes.func,
  currentAssignedList: PropTypes.array,
  onValidationChange: PropTypes.func,
  isLoading: PropTypes.bool,
  timeZoneOptions: PropTypes.array,
  showAssignmentTitle: PropTypes.bool,
  excludeTitles: PropTypes.array,
  titleValidationConfig: PropTypes.object
};

AssignmentListMainComponent.defaultProps = {
  parentApp: 'aet',
  textInfo: {},
  assignmentConfig: {},
  isBulkEditMode: false,
  chapterMcqMapping: null,
  renderInModal: false,
  onUploadComplete: () => {},
  onSaveAndAdd: null,
  editingAssignment: null,
  editingIndex: null,
  handleAddAssignemntPopupClose: () => {},
  onDeleteComplete: () => {},
  onEditComplete: () => {},
  assignmentFormActions: {},
  showListView: true,
  restrictAlreadyAssigned: true,
  currentAssignmentIdx: null,
  addAssignmentCount: 0,
  showCheckboxes: false,
  showPublishButton: false,
  onDeleteReady: () => {},
  handleUploadingScreenChange: () => {},
  currentAssignedList: [],
  onValidationChange: () => {},
  isLoading: false,
  timeZoneOptions: [],
  showAssignmentTitle: false,
  excludeTitles: [],
  titleValidationConfig: {}
};
export default AssignmentListMainComponent;
